import { AxiosResponse, AxiosError } from "axios";
import api from "@services/axios/apiManager";

//API - 經緯度取得地址（回傳結構可能為 res.data.data 或 res.data）
export const handleAPIGeocodeGetAddress = async (geocode: { lat: number; lng: number }, lineAccessToken: string) => {
  try {
    const res: AxiosResponse = await api.get(`address-normalization-api/address?lat=${geocode.lat}&lng=${geocode.lng}`, {
      baseURLType: "ADDRESS",
      headers: {
        Authorization: `Bearer ${lineAccessToken}`,
      },
    });
    const data = res?.data as Record<string, unknown> | undefined;
    const possibleList =
      (data?.data as { possibleList?: unknown[] } | undefined)?.possibleList ??
      (data?.possibleList as unknown[] | undefined) ??
      [];
    return { state: "success" as const, possibleList: Array.isArray(possibleList) ? possibleList : [] };
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("地址轉換失敗:", axiosError);
    return undefined;
  }
};

//API - 地址取得經緯度（/address/repair，後端 provider 順序 Polstar → Google → TomTom → HERE）
//     回傳第一筆帶座標的建議；查無或無座標時回傳 null，由呼叫端決定是否 fallback
export const handleAPIAddressToGeocode = async (
  address: string,
  lineAccessToken: string,
): Promise<{ lat: number; lng: number } | null> => {
  try {
    const res: AxiosResponse = await api.get(`address-normalization-api/address/repair?address=${encodeURIComponent(address)}`, {
      baseURLType: "ADDRESS",
      headers: {
        Authorization: `Bearer ${lineAccessToken}`,
      },
    });
    const data = res?.data as Record<string, unknown> | undefined;
    const possibleList =
      (data?.data as { possibleList?: unknown[] } | undefined)?.possibleList ??
      (data?.possibleList as unknown[] | undefined) ??
      [];
    if (!Array.isArray(possibleList)) return null;
    //-- Polstar 只回縣市層級時不帶座標 → 取第一筆真的有經緯度的
    for (const item of possibleList as Record<string, unknown>[]) {
      const lat = Number(item?.lat);
      const lng = Number(item?.lng);
      if (item?.lat != null && item?.lng != null && !Number.isNaN(lat) && !Number.isNaN(lng)) {
        return { lat, lng };
      }
    }
    return null;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("地址取得座標失敗:", axiosError);
    return null;
  }
};

//API - 地址切分
export interface AddressComponent {
  [key: string]: string | undefined;
  administrative_area_level_1?: string;
  administrative_area_level_2?: string;
  route?: string;
  street_number?: string;
}

interface APIResult<T> {
  state: "success" | "error";
  data: T;
}

export const handleAPIAddressComponents = async (
  addressList: string[],
  lineAccessToken: string,
): Promise<APIResult<AddressComponent[] | AxiosError>> => {
  try {
    const res: AxiosResponse = await api.post(
      `address-normalization-api/address/components`,
      { addresses: addressList },
      {
        baseURLType: "ADDRESS",
        headers: {
          Authorization: `Bearer ${lineAccessToken}`,
        },
      },
    );
    return { state: "success", data: res?.data };
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("地址轉換失敗:", axiosError);
    return { state: "error", data: axiosError };
  }
};

//API - 取得縣市 List
export const handleAPIGetCity = async (lineAccessToken: string) => {
  try {
    const res: AxiosResponse = await api.get("address-normalization-api/address/valid/city", {
      baseURLType: "ADDRESS",
      headers: {
        Authorization: `Bearer ${lineAccessToken}`,
      },
    });
    return { state: "success", list: res.data.data };
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("取得縣市列表失敗:", axiosError);
  }
};

//API - 取得鄉鎮市區 List
export const handleAPIGetTown = async (lineAccessToken: string, city: string) => {
  try {
    const res: AxiosResponse = await api.get(`address-normalization-api/address/valid/town?city=${city}`, {
      baseURLType: "ADDRESS",
      headers: {
        Authorization: `Bearer ${lineAccessToken}`,
      },
    });
    return { state: "success", list: res.data.data };
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("取得鄉鎮市區列表失敗:", axiosError);
  }
};
